import type { FC } from 'react';
import type { TextFieldProps, AutocompleteProps } from '@mui/material';
import { Autocomplete, Checkbox, TextField, Typography } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import type * as React from 'react';
import type { AutocompleteRenderOptionState } from '@mui/material/Autocomplete/Autocomplete';
import type { AutocompleteSize } from 'types/utils';
import type { Option, OptionValue } from './useCheckboxAutocomplete';
import useCheckboxAutocomplete from './useCheckboxAutocomplete';

export type { Option, OptionValue } from './useCheckboxAutocomplete';

interface CheckboxAutocompleteProps extends Pick<
  AutocompleteProps<OptionValue, true, boolean, false>,
  'disableClearable'
> {
  value?: OptionValue[];
  onChange: (value: OptionValue[]) => void;
  InputProps?: TextFieldProps;
  id: string;
  isLoading?: boolean;
  options: Option[];
  allSelectable?: boolean;
  size?: AutocompleteSize;
}

const CheckboxAutocomplete: FC<CheckboxAutocompleteProps> = ({
  id,
  options = [],
  isLoading,
  onChange,
  value,
  InputProps,
  allSelectable = true,
  ...props
}: CheckboxAutocompleteProps) => {
  const {
    isFocused,
    inputValue,
    selectedOptions,
    listOptions,
    isOptionChecked,
    onFocus,
    onBlur,
    handleChange,
    handleInputChange,
  } = useCheckboxAutocomplete({ options, value, allSelectable, onChange });

  const optionRenderer = (
    renderProps: React.HTMLAttributes<HTMLLIElement>,
    option: Option,
    { selected }: AutocompleteRenderOptionState
  ) => {
    const isChecked = isOptionChecked(option, selected);
    const uniqueKey =
      typeof option.value === 'object' ? `option-${JSON.stringify(option.value)}` : `option-${option.value}`;

    return (
      <li {...renderProps} key={uniqueKey} aria-selected={isChecked}>
        <Checkbox icon={<CheckBoxOutlineBlankIcon />} checkedIcon={<CheckBoxIcon />} checked={isChecked} />
        {option.label}
      </li>
    );
  };

  return (
    <Autocomplete
      {...props}
      multiple
      disabled={isLoading ?? InputProps?.disabled}
      loading={isLoading}
      id={id}
      options={options}
      disableCloseOnSelect
      value={selectedOptions}
      getOptionLabel={(option) => option.label}
      renderOption={optionRenderer}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onFocus={onFocus}
      onBlur={onBlur}
      open={isFocused}
      renderInput={(params) => <TextField {...params} label={InputProps?.label} />}
      selectOnFocus={true}
      renderTags={(items: Option[]) => (
        <Typography
          sx={(t) =>
            isFocused && props.size === 'small'
              ? {
                  visibility: 'hidden',
                  fontSize: t.typography.body2.fontSize,
                }
              : {
                  fontSize: props.size === 'small' ? t.typography.body2.fontSize : t.typography.body1.fontSize,
                }
          }
        >
          {InputProps?.label}{' '}
          {items.length !== options.length && (
            <Typography
              component="span"
              color="black"
              sx={(t) => ({
                '&': {
                  p: '0',
                  fontSize: props.size === 'small' ? t.typography.body2.fontSize : t.typography.body1.fontSize,
                },
              })}
            >
              ({items.length})
            </Typography>
          )}
        </Typography>
      )}
      filterOptions={() => listOptions}
      componentsProps={{
        popper: {
          placement: 'bottom-start',
        },
      }}
    />
  );
};

export default CheckboxAutocomplete;
